// IE Hub - Visibility DSL
// Safe, minimal expression language for evaluating field visibility

import { pathGet } from '../../../shared/lib/pathGet';
import { calculateAge as calculateAgeYears } from '@/shared/lib/utils/others';

// Type definitions
export interface VariableNode {
  var: string;
}

export interface OperatorNode {
  op: string;
  args: VisibilityNode[];
}

export type VisibilityNode = 
  | string 
  | number 
  | boolean 
  | null 
  | VariableNode 
  | OperatorNode;

export type VisibilityData = Record<string, unknown>;

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

/**
 * Convert camelCase to UPPER_SNAKE_CASE for API field name matching
 * e.g., 'basicIncome' -> 'BASIC_INCOME'
 */
function camelToUpperSnake(str: string): string {
  return str
    .replace(/([A-Z])/g, '_$1')
    .toUpperCase()
    .replace(/^_/, '');
}

// Operator implementations. Keep this list small and auditable.
// Most return boolean, but some (like size, calculateAge) return numbers for use in comparisons
const operators: Record<string, (args: unknown[]) => boolean | number> = {
  // Logical
  and: (args: unknown[]) => args.every(Boolean),
  or: (args: unknown[]) => args.some(Boolean),
  not: (args: unknown[]) => !args[0],

  // Comparison
  eq: (args: unknown[]) => args[0] === args[1],
  ne: (args: unknown[]) => args[0] !== args[1],
  gt: (args: unknown[]) => Number(args[0]) > Number(args[1]),
  gte: (args: unknown[]) => Number(args[0]) >= Number(args[1]),
  lt: (args: unknown[]) => Number(args[0]) < Number(args[1]),
  lte: (args: unknown[]) => Number(args[0]) <= Number(args[1]),

  // String predicates
  includes: (args: unknown[]) => {
    const [collection, item] = args;
    if (typeof collection === 'string') return collection.includes(String(item));
    if (Array.isArray(collection)) return collection.includes(item);
    return false;
  },
  startsWith: (args: unknown[]) => (typeof args[0] === 'string' ? args[0].startsWith(String(args[1])) : false),
  endsWith: (args: unknown[]) => (typeof args[0] === 'string' ? args[0].endsWith(String(args[1])) : false),
  size: (args: unknown[]) => {
    const target = args[0];
    if (Array.isArray(target) || typeof target === 'string') {
      return target.length;
    }
    if (target instanceof Map || target instanceof Set) {
      return target.size;
    }
    if (target && typeof target === 'object') {
      return Object.keys(target as Record<string, unknown>).length;
    }
    return 0;
  },
  
  // Array membership check
  in: (args: unknown[]) => {
    const [value, array] = args;
    if (!Array.isArray(array)) return false;
    return array.includes(value);
  },
  
  // Prompt system operators
  // Calculate age from date of birth string
  calculateAge: (args: unknown[]) => {
    const dobStr = args[0];
    const age = calculateAgeYears(
      typeof dobStr === "string" || dobStr instanceof Date ? (dobStr as any) : undefined
    );
    return typeof age === "number" && Number.isFinite(age) ? age : 0;
  },
  
  // Check if amount exceeds trigger limit from _triggers context
  // Note: This needs to be called with data context containing _triggers
  // Will return false if no triggers found
  hasLimitExceeded: (_args: unknown[]) => {
    // This operator is evaluated differently - it needs access to data context
    // The actual logic is in evaluateNode where we have access to data
    return false;
  },
  
  // Sum multiple field values
  sumFields: (args: unknown[]) => {
    return args.reduce((sum: number, val) => {
      const num = Number(val);
      return sum + (isNaN(num) ? 0 : num);
    }, 0) as number;
  },
  
  // Addition operator (alias for sumFields)
  '+': (args: unknown[]) => {
    return args.reduce((sum: number, val) => {
      const num = typeof val === 'number' ? val : parseFloat(String(val));
      return sum + (isNaN(num) ? 0 : num);
    }, 0) as number;
  },
};

export function evaluateNode(node: VisibilityNode, data: VisibilityData): unknown {
  // Literals
  if (node === null || typeof node !== 'object') return node;

  // Variable reference: { "var": "path.to.value" }
  if (isPlainObject(node) && Object.prototype.hasOwnProperty.call(node, 'var')) {
    const varNode = node as VariableNode;
    const path = varNode.var;
    if (typeof path !== 'string') return undefined;
    return pathGet(data, path);
  }

  // Conditional (if-then-else): { "if": [condition, thenValue, elseValue] }
  if (isPlainObject(node) && Object.prototype.hasOwnProperty.call(node, 'if')) {
    const ifNode = node as { if: unknown[] };
    if (Array.isArray(ifNode.if) && ifNode.if.length >= 2) {
      const condition = evaluateNode(ifNode.if[0], data);
      if (condition) {
        return evaluateNode(ifNode.if[1], data);
      } else if (ifNode.if.length >= 3) {
        return evaluateNode(ifNode.if[2], data);
      }
      return undefined;
    }
  }

  // Operator application: { op: 'eq', args: [ ... ] }
  if (isPlainObject(node) && typeof (node as OperatorNode).op === 'string') {
    const opNode = node as OperatorNode;
    
    // Special handling for hasLimitExceeded which needs data context
    if (opNode.op === 'hasLimitExceeded') {
      const args = Array.isArray(opNode.args) ? opNode.args.map((arg) => evaluateNode(arg, data)) : [];
      const [amount, apiFieldName] = args;
      const numAmount = Number(amount);
      
      // Get triggers from data context
      const triggers = data._triggers as Array<{
        columnName: string;
        fieldName: string;
        upperLimit?: number;
        lowerLimit?: number;
      }>;
      
      if (Array.isArray(triggers) && typeof apiFieldName === 'string') {
        // Convert camelCase to UPPER_SNAKE_CASE to match API columnName format
        // e.g., 'basicIncome' -> 'BASIC_INCOME'
        const upperSnakeCase = camelToUpperSnake(apiFieldName);
        
        const trigger = triggers.find(
          (t) => t.columnName === upperSnakeCase || t.fieldName === apiFieldName
        );
        
        if (trigger) {
          // Check upper limit
          if (trigger.upperLimit !== undefined && numAmount > trigger.upperLimit) {
            return true;
          }
          // Check lower limit
          if (trigger.lowerLimit !== undefined && numAmount < trigger.lowerLimit) {
            return true;
          }
        }
      }
      return false;
    }
    
    const op = operators[opNode.op];
    if (typeof op !== 'function') return false;
    const args = Array.isArray(opNode.args) ? opNode.args.map((arg) => evaluateNode(arg, data)) : [];
    return op(args);
  }

  // Fallback: invalid structure evaluates to false
  return false;
}

export function evaluateVisibilityRule(rule: VisibilityNode, data: VisibilityData): boolean {
  return Boolean(evaluateNode(rule, data));
}

export default {
  evaluateNode,
  evaluateVisibilityRule,
};
